import React from 'react';
import styled from 'styled-components';
import SectionHeading from '../../components/common/SectionHeading';

const Wrapper = styled.section`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 1.5rem;
  padding: 2.5rem;
  box-shadow: ${({ theme }) => theme.shadows.md};
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr);
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
    padding: 1.75rem;
  }
`;

const MapBox = styled.div`
  border-radius: 1.5rem;
  overflow: hidden;
  min-height: 500px;
  width: 100%;
  box-shadow: ${({ theme }) => theme.shadows.xl};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${({ theme }) => theme.colors.primary}20;
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary}40;
    box-shadow: ${({ theme }) => theme.shadows.lg};
    transform: translateY(-2px);
  }
`;

const MapFrame = styled.iframe`
  width: 100%;
  height: 100%;
  min-height: 500px;
  border: 0;
`;

const ExternalMapLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  align-items: center;
  margin-top: 1rem;
`;

const AddressBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  justify-content: center;
`;

const AddressCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 1.5rem;
  padding: 2.5rem;
  box-shadow: ${({ theme }) => theme.shadows.md};
  border: 2px solid ${({ theme }) => theme.colors.primary}15;
  position: relative;
  overflow: hidden;
  transition: ${({ theme }) => theme.transitions.default};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary}30;
    box-shadow: ${({ theme }) => theme.shadows.lg};
    transform: translateY(-2px);
  }
`;

const AddressHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.surfaceMuted};
`;

const AddressIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  box-shadow: 0 4px 12px ${({ theme }) => theme.colors.primary}25;
`;

const AddressLabel = styled.h3`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const AddressText = styled.p`
  margin: 0;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 1.125rem;
  font-weight: 500;
  padding-left: 0.5rem;
  word-break: keep-all;
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.surfaceMuted};
  margin: 2rem 0;
`;

const ExternalMapLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 0.9rem;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.surfaceMuted};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.875rem;
  font-weight: 600;
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    background: ${({ theme }) => theme.colors.primary}14;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const aceCodingAddress = '경기도 고양시 덕양구 향기로 148, 101호(에이스코딩학원)';
const academyCoordinates = { lat: 37.602327, lng: 126.896413 };
const osmEmbedUrl = `https://www.openstreetmap.org/export/embed.html?bbox=126.8905%2C37.5989%2C126.9023%2C37.6058&layer=mapnik&marker=${academyCoordinates.lat}%2C${academyCoordinates.lng}`;
const googleMapUrl = `https://www.google.com/maps/search/?api=1&query=${academyCoordinates.lat},${academyCoordinates.lng}`;
const kakaoMapUrl = `https://map.kakao.com/link/map/${encodeURIComponent('에이스코딩학원')},${academyCoordinates.lat},${academyCoordinates.lng}`;
const naverMapUrl = `https://map.naver.com/v5/search/${encodeURIComponent('경기도 고양시 덕양구 향기로 148')}`;

const LocationPage = () => {
  return (
    <Wrapper>
      <MapBox>
        <MapFrame
          title="에이스코딩학원 위치 지도"
          src={osmEmbedUrl}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </MapBox>
      <AddressBox>
        <SectionHeading eyebrow="오시는 길" title="위치안내" />
        <AddressCard>
          <AddressHeader>
            <AddressIcon>📍</AddressIcon>
            <AddressLabel>주소</AddressLabel>
          </AddressHeader>
          <AddressText>
            {aceCodingAddress}
            <br />
            <span style={{ fontSize: '0.95rem', color: '#666', marginTop: '0.5rem', display: 'block' }}>
              *향동중학교 건너편
            </span>
          </AddressText>
          <ExternalMapLinks>
            <ExternalMapLink href={naverMapUrl} target="_blank" rel="noreferrer">
              네이버지도에서 보기
            </ExternalMapLink>
            <ExternalMapLink href={kakaoMapUrl} target="_blank" rel="noreferrer">
              카카오맵에서 보기
            </ExternalMapLink>
            <ExternalMapLink href={googleMapUrl} target="_blank" rel="noreferrer">
              구글지도에서 보기
            </ExternalMapLink>
          </ExternalMapLinks>
          <Divider />
          <AddressText style={{ fontSize: '1rem' }}>
            <strong>DMC역에서 오시는 방법:</strong>
            <br />
            <br />
            • 버스: 075A, 730번 버스 이용
            <br />
            • 하차: 향동DMC하우스디.중흥S클래스역 하차
          </AddressText>
        </AddressCard>
      </AddressBox>
    </Wrapper>
  );
};

export default LocationPage;